// Types
import Fund from '@/types/fund';
import PERIOD_END_DATA_OPTIONS from '@/types/period-end-data-options';
import Shelf from '@/types/shelf';

// Utils
import { generateFundFromCustomYieldConfig } from '@/utils/portfolio/utils';
import CustomYieldParams from '@/types/custom-yield-params';

// The cache key should uniquely identify the custom yield config, currency, id, and monthEndDataOption
export const customYieldCache = new Map<string, Promise<Fund>>();

/**
 * Retrieves a cached custom yield fund based on specified parameters, or generates a new one if not found.
 * @param {any} yieldParams - Used to calculate the custom yield for a fund.
 * @param options - Object with the following optional properties:
 *  filteredShelves?: any;
    complianceComparisons?: any;
 * @param {string} id - ID of custom yield fund.
 * @param {string} [currency] - Optional param indicating currency in which the fund data should be returned. If
 * provided, it will be used in generating the cache key for the fund data. If not provided, the
 * default currency may be used
 * @param {any} [monthEndDataOption] - Optional param used to specify additional
 * options related to month-end data for the fund. 
 * @returns a Promise that resolves to a `Fund` object.
 */
export function getCachedCustomYieldFund(
  yieldParams: CustomYieldParams,
  shelfInfo: {
    filteredShelves?: Shelf[];
    complianceComparisons?: string[] | undefined;
  },
  currency: string | undefined,
  id?: string,
  monthEndDataOption?: PERIOD_END_DATA_OPTIONS,
): Promise<Fund> {
  const cacheKey = `${id}-${JSON.stringify(yieldParams)}-${currency}`;
  if (customYieldCache.has(cacheKey)) {
    return customYieldCache.get(cacheKey)!;
  }

  const promise = Promise.resolve(generateFundFromCustomYieldConfig(yieldParams, shelfInfo, currency, id, monthEndDataOption));

  customYieldCache.set(cacheKey, promise);
  return promise;
}

export function clearCustomYieldCache() {
  customYieldCache.clear();
}
